import React, { useState, useEffect, useRef } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { 
  Search, 
  ShoppingCart, 
  User, 
  ChevronDown, 
  Menu, 
  X, 
  Sun,
  Moon,
  Heart,
  Shield,
  Truck,
  Award
} from "lucide-react";
// import { Button } from "./ui/button";
// import { Input } from "./ui/input";
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from "./ui/dropdown-menu";
import { useTheme } from "../contexts/ThemeContext";
import { useGlobalData } from "../contexts/GlobalDataContext";

import { searchProducts } from "../api/productApi";
import { BRAND_ICONS, getMatchedBrandKey } from "../pages/Home/components/BrandLogos";

const formatPriceLabel = (deal) => {
  const formatNum = (num) => {
    const kVal = Math.round(num / 1000);
    return `₹${kVal}K`;
  };

  if (deal.condition === 'ABOVE') {
    return `Above ${formatNum(deal.price)}`;
  } else if (deal.minPrice === 0 || !deal.minPrice) {
    return `Under ${formatNum(deal.price)}`;
  } else {
    return `${formatNum(deal.minPrice)} - ${formatNum(deal.price)}`;
  }
};

const Header = ({ cartCount = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const { categories, categoriesLoading, brands, bestDeals, products } = useGlobalData();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const headerNavRef = useRef(null);
  const megaMenuRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideNav = headerNavRef.current?.contains(event.target);
      const clickedInsideMegaMenu = megaMenuRef.current?.contains(event.target);

      if (!clickedInsideNav && !clickedInsideMegaMenu) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync search query with URL params
  useEffect(() => {
    const urlSearchParam = searchParams.get("search");
    if (urlSearchParam !== searchQuery) {
      setSearchQuery(urlSearchParam || "");
    }
  }, [searchParams]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedQuery = searchQuery.trim();
      const newSearchParams = new URLSearchParams(searchParams);
      if (trimmedQuery.length > 0) {
        newSearchParams.set("search", trimmedQuery);
        performSearch(trimmedQuery);
      } else {
        newSearchParams.delete("search");
        setSearchResults([]);
        setShowSearchResults(false);
      }
      setSearchParams(newSearchParams, { replace: true });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    try {
      setSearchLoading(true);
      const response = await searchProducts({ q: query, limit: 5 });
      if (response.success && response.data?.products) {
        setSearchResults(response.data.products);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error("Error searching products:", error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchResults(false);
      navigate(`/all-products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSearchResultClick = (product) => {
    setSearchQuery("");
    setShowSearchResults(false);
    navigate(`/product/${product._id || product.id}`);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim().length === 0) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleInputFocus = () => {
    if (searchResults.length > 0) setShowSearchResults(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSearchResults(false), 200);
  };

  // Trust badges data
  const trustBadges = [
    { icon: Shield, text: "6 Month Warranty", color: "text-green-600" },
    { icon: Award, text: "4.7 Rating", color: "text-yellow-600" },
    { icon: Truck, text: "Free Shipping", color: "text-blue-600" },
  ];

  return (
    <>
      {/* Top Trust Bar - New Addition */}
      <div className={`font-gilroy hidden md:block border-b border-gray-800 transition-all duration-300 overflow-hidden relative ${
        isScrolled ? 'hidden' : 'block'
      }`} style={{ background: "#000" }}>
        <div className="flex items-center h-10 text-sm whitespace-nowrap animate-scroll-left w-max hover:[animation-play-state:paused]">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center space-x-12 px-6">
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <badge.icon className={`w-4 h-4 ${badge.color}`} />
                  <span className="text-gray-300 font-medium">{badge.text}</span>
                </div>
              ))}
              {/*<div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-gray-300 font-medium"> EMI Available</span>
              </div>*/}
              <div className="flex items-center space-x-2">
                <span className="text-yellow-500">★</span>
                <span className="text-gray-300 font-medium">20000+ Happy Customers</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`font-gilroy sticky top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/95 dark:bg-gray-950/95 backdrop-blur-md shadow-lg border-b border-gray-200/50 dark:border-gray-700/50"
            : "border-b border-gray-200 dark:border-gray-700"
        }`}
        style={!isScrolled ? { background: "var(--surface-header)" } : {}}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo + Nav grouped on left side */}
            <div className="flex items-center">
              {/* Logo with hover effect */}
              <Link to="/" className="flex items-center space-x-2 group mr-12">
                <img
                  src="/lapzop-logo.png"
                  alt="Lapzop"
                  className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-1" ref={headerNavRef}>
                <Link
                  to="/"
                  className={`relative px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 rounded-lg ${
                    location.pathname === '/'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  Home
                </Link>
                
                {/* Brands Mega Menu Trigger */}
                <div className="relative">
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === 'Brands' ? null : 'Brands')}
                    onMouseEnter={() => setActiveDropdown('Brands')}
                    className={`flex items-center space-x-1 px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 rounded-lg ${
                      activeDropdown === 'Brands'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    <span>Brands</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'Brands' ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Budget Small Dropdown */}
                <div className="relative" onMouseEnter={() => setActiveDropdown('Budget')} onMouseLeave={() => setActiveDropdown(null)}>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === 'Budget' ? null : 'Budget')}
                    className={`flex items-center space-x-1 px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 rounded-lg ${
                      activeDropdown === 'Budget'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    <span>By Budget</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'Budget' ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <div
                    className={`absolute left-0 top-full mt-2 w-48 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl transition-all duration-200 ease-in-out z-50 ${
                      activeDropdown === 'Budget' ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
                    }`}
                    style={{ background: "var(--surface-elevated)" }}
                  >
                    <div className="py-2">
                      {bestDeals?.length > 0 ? bestDeals.map((deal) => {
                        const filterUrl = deal.condition === 'ABOVE'
                          ? `/all-products?minPrice=${deal.price}`
                          : `/all-products?minPrice=${deal.minPrice || 0}&maxPrice=${deal.price}`;
                        return (
                          <Link
                            key={deal._id || filterUrl}
                            to={filterUrl}
                            onClick={() => setActiveDropdown(null)}
                            className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            {formatPriceLabel(deal)}
                          </Link>
                        );
                      }) : <div className="px-4 py-2 text-sm text-gray-500">No budget options</div>}
                    </div>
                  </div>
                </div>

                {/* Category Mega Menu Trigger */}
                <div className="relative">
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === 'Category' ? null : 'Category')}
                    onMouseEnter={() => setActiveDropdown('Category')}
                    className={`flex items-center space-x-1 px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 rounded-lg ${
                      activeDropdown === 'Category'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    <span>Category</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === 'Category' ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <Link
                  to="/contact"
                  className={`relative px-3 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 rounded-lg ${
                    location.pathname === '/contact'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  Contact Us
                </Link>
              </nav>
            </div>

            {/* Right side actions with improved styling */}
            <div className="flex items-center space-x-2">
              {/* Enhanced Search Bar */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center">
                <div className="relative w-64 lg:w-80">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search 20000+ products..."
                      value={searchQuery}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="w-full !rounded-full border-2 border-gray-200 dark:border-gray-700 py-2.5 pl-5 pr-12 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 transition-[border-color,box-shadow] duration-300"
                      style={{ background: "var(--surface-input)" }}
                    />
                    <button
                      type="submit"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center text-white rounded-full w-9 h-9 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                      style={{ background: "var(--brand-blue)" }}
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Enhanced Search Results Dropdown */}
                  {showSearchResults && searchQuery.trim().length > 0 && (
                    <div
                      className="absolute top-full left-0 right-0 mt-2 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 max-h-96 overflow-y-auto animate-in slide-in-from-top-2 duration-200"
                      style={{ background: "var(--surface-elevated)" }}
                    >
                      {searchLoading ? (
                        <div className="p-6 text-center">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <p className="mt-2 text-gray-500 dark:text-gray-400 text-sm">Searching...</p>
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div className="p-6 text-center">
                          <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500 dark:text-gray-400">No products found for "{searchQuery}"</p>
                        </div>
                      ) : (
                        <>
                          <div className="max-h-80 overflow-y-auto">
                            {searchResults.map((product) => {
                              const productId = product._id || product.id;
                              const productImage = product.images?.length > 0 ? product.images[0] : "/placeholder-image.jpg";
                              const productName = product.name || "Product";
                              const productPrice = product.basePrice || product.mrp || 0;
                              return (
                                <div
                                  key={productId}
                                  onClick={() => handleSearchResultClick(product)}
                                  className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-200"
                                >
                                  <img
                                    src={productImage}
                                    alt={productName}
                                    className="w-12 h-12 object-cover rounded-lg"
                                    onError={(e) => { e.target.src = "/placeholder-image.jpg"; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{productName}</p>
                                    {product.brand && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{product.brand}</p>}
                                    <p className="text-sm font-bold text-blue-600 dark:text-blue-400 mt-1">₹{productPrice.toLocaleString()}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div
                            onClick={handleSearch}
                            className="p-3 text-center text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-t border-gray-200 dark:border-gray-700 transition-colors duration-200"
                          >
                            View all {searchResults.length}+ results for "{searchQuery}" →
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </form>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800 group"
                aria-label="Toggle Dark Mode"
              >
                {isDark ? (
                  <Sun className="w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform duration-300 hover:rotate-90 group-hover:scale-110" />
                ) : (
                  <Moon className="w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform duration-300 hover:-rotate-90 group-hover:scale-110" />
                )}
              </button>

              {/* Enhanced Cart */}
              {!!localStorage.getItem("token") && (
                <Link to="/cart" className="relative group">
                  <div className="relative p-2 rounded-full transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <ShoppingCart className="w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform duration-300 group-hover:scale-110" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-lg animate-pulse">
                        {cartCount > 99 ? "99+" : cartCount}
                      </span>
                    )}
                  </div>
                </Link>
              )}

              {/* Enhanced User Profile */}
              <Link to="/profile">
                <div className="p-2 rounded-full transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer group">
                  <User className="w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform duration-300 group-hover:scale-110" />
                </div>
              </Link>

              {/* Mobile Menu Toggle with animation */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700 dark:text-gray-300 animate-in rotate-90 duration-200" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Navigation */}
        <div
          className={`lg:hidden fixed inset-x-0 top-20 z-40 transform transition-all duration-500 ease-in-out ${
            isMenuOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0 pointer-events-none"
          }`}
          style={{ background: "var(--surface-header)", height: "calc(100vh - 80px)" }}
        >
          <div className="h-full overflow-y-auto py-4">
            <div className="container mx-auto px-4">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="md:hidden mb-6">
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 py-3 pl-5 pr-12 text-sm outline-none focus:border-blue-400 transition-all"
                    style={{ background: "var(--surface-input)" }}
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white rounded-lg p-2 transition-all hover:scale-105"
                    style={{ background: "var(--brand-blue)" }}
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </form>

              {/* Mobile Navigation Links */}
              <div className="flex flex-col space-y-2">
                {[
                  { to: "/", label: "Home" },
                  { to: "/contact", label: "Contact Us" },
                ].map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className="px-4 py-3 text-base font-semibold text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {label}
                  </Link>
                ))}
                
                {/* Dynamic Links Section for Mobile */}
                {/* Categories */}
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Categories</p>
                  <div className="space-y-1">
                    {categoriesLoading ? (
                      <div className="py-2 text-gray-500">Loading...</div>
                    ) : categories.length === 0 ? (
                      <div className="py-2 text-gray-500">No categories</div>
                    ) : (
                      categories.map((category) => {
                        const categorySlug = category.slug || category.name?.toLowerCase().replace(/\s+/g, "-");
                        return (
                          <Link
                            key={category._id || category.id}
                            to={`/all-products?category=${categorySlug}`}
                            className="block py-2 text-sm text-gray-700 dark:text-gray-300 pl-4 hover:text-blue-600 transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/all-products?category=${categorySlug}`);
                              setIsMenuOpen(false);
                            }}
                          >
                            {category.name}
                          </Link>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Brands */}
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Brands</p>
                  <div className="space-y-1 flex flex-wrap gap-2">
                    {brands.map((brand) => {
                      const icon = BRAND_ICONS[getMatchedBrandKey(brand.name)];
                      return (
                        <Link
                          key={brand.id}
                          to={`/all-products?brand=${encodeURIComponent(brand.name)}`}
                          className="py-1.5 px-3 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-700 dark:text-gray-300 flex items-center space-x-2"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/all-products?brand=${encodeURIComponent(brand.name)}`);
                            setIsMenuOpen(false);
                          }}
                        >
                          {icon ? (
                            <div className="w-6 h-4 flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full text-gray-700 dark:text-gray-300">
                              {icon}
                            </div>
                          ) : brand.logo ? (
                            <img src={brand.logo} alt={brand.name} className="w-4 h-4 object-contain" />
                          ) : null}
                          <span>{brand.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Budget */}
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Budget</p>
                  <div className="space-y-1 flex flex-wrap gap-2">
                    {bestDeals?.map((deal) => {
                      const filterUrl = deal.condition === 'ABOVE'
                        ? `/all-products?minPrice=${deal.price}`
                        : `/all-products?minPrice=${deal.minPrice || 0}&maxPrice=${deal.price}`;
                      return (
                        <Link
                          key={deal._id || filterUrl}
                          to={filterUrl}
                          className="py-1 px-3 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-700 dark:text-gray-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {formatPriceLabel(deal)}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reverted Mega Menu style for all dropdowns */}
        <div
          ref={megaMenuRef}
          onMouseLeave={() => setActiveDropdown(null)}
          className={`absolute left-0 right-0 w-full border-b border-gray-200 dark:border-gray-700 shadow-2xl transition-all duration-300 ease-in-out ${
            (activeDropdown === 'Brands' || activeDropdown === 'Category') ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
          }`}
          style={{ top: "80px", zIndex: 49, background: "var(--surface-elevated)" }}
        >
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {activeDropdown === 'Brands' && (
                brands?.length > 0 ? brands.map((brand) => {
                  const icon = BRAND_ICONS[getMatchedBrandKey(brand.name)];
                  return (
                    <Link
                      key={brand.id}
                      to={`/all-products?brand=${encodeURIComponent(brand.name)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/all-products?brand=${encodeURIComponent(brand.name)}`);
                        setActiveDropdown(null);
                      }}
                      className="group flex items-center p-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-950/30 dark:hover:to-blue-900/30 transition-all duration-300"
                    >
                      {icon ? (
                        <div className="w-10 h-6 mr-3 flex items-center justify-center text-gray-700 dark:text-gray-300 group-hover:scale-110 transition-transform duration-300 [&>svg]:max-w-full [&>svg]:max-h-full">
                          {icon}
                        </div>
                      ) : brand.logo ? (
                        <img src={brand.logo} alt={brand.name} className="w-8 h-8 object-contain mr-3 group-hover:scale-110 transition-transform duration-300" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-500 mr-3">{brand.name?.charAt(0)}</div>
                      )}
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{brand.name}</span>
                    </Link>
                  );
                }) : <div className="col-span-full py-8 text-center text-gray-500">No brands available</div>
              )}

              {activeDropdown === 'Category' && (
                categories?.length > 0 ? categories.map((category) => {
                  const categorySlug = category.slug || category.name?.toLowerCase().replace(/\s+/g, "-");
                  return (
                    <Link
                      key={category._id || category.id}
                      to={`/all-products?category=${categorySlug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/all-products?category=${categorySlug}`);
                        setActiveDropdown(null);
                      }}
                      className="group flex items-center p-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-950/30 dark:hover:to-blue-900/30 transition-all duration-300"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{category.name}</span>
                    </Link>
                  );
                }) : <div className="col-span-full py-8 text-center text-gray-500">No categories available</div>
              )}
            </div>
          </div>
        </div>

      </header>
    </>
  );
};

export default Header;
